import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, Field } from "formik";
import { Row, Col, Collapse } from "reactstrap";

import moment from "moment";

import { Input, LabeledDatePicker } from "../../../../components/formik";
import { optionsType } from "../../../../prop-types";
import {
  exportLogsCsvData,
  exportLogsCsvLoading,
  logsFiltersSelector,
  logsIsLoadingSelector,
} from "../../selectors";
import { ButtonWithSpinner } from "../../../../components/common";
import Clickable from "../../../../components/common/Clickable";
import { CollapseButton, CollapseOpenIcon } from "../../../../assets";
import "./searchLogsForm.css";
import {
  clearExportLogsCsvResult,
  exportLogsCsv,
  getLogsCount,
  setLogSearchFilters,
} from "../../actions";
import { FaTimes } from "react-icons/fa";
import {
  FILTERS_TEMPLATE,
  LOGS_FILTERS_LABELS,
} from "../../constants/logFilters";
import ButtonSelectLogsType from "../../../../components/formik/logs-list/ButtonSelectLogsType";
import { trimFormData } from "../../../../utilities/filters";
import ButtonExport from "../../../../components/common/ButtonExport/ButtonExport";
import { FiUpload } from "react-icons/fi";
import { notifyWarning } from "../../../../actions";
import { isEmpty } from "ramda";

const SearchLogsForm = ({
  logTypeOptions,
  toggleCollapse,
  isOpenCollapse,
  entriesLogs,
}) => {
  const dispatch = useDispatch();
  const filters = useSelector(logsFiltersSelector);
  const isLoading = useSelector(logsIsLoadingSelector);
  const formikRef = useRef(null);
  const isExportLoading = useSelector(exportLogsCsvLoading);
  const csvFile = useSelector(exportLogsCsvData);

  useEffect(
    () => () => {
      dispatch(clearExportLogsCsvResult());
    },
    []
  );

  useEffect(() => {
    if (!isEmpty(csvFile) && csvFile.fileBytes.size <= 3) {
      dispatch(notifyWarning("There are no logs for export"));
    } else if (!isEmpty(csvFile) && csvFile.fileBytes.size > 3) {
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(csvFile.fileBytes, csvFile.fileName);
        return;
      }

      const url = URL.createObjectURL(csvFile.fileBytes);
      const link = document.createElement("a");
      link.href = url;
      link.download = csvFile.fileName;
      link.click();
      setTimeout(() => {
        dispatch(clearExportLogsCsvResult());

        window.URL.revokeObjectURL(url);
      }, 100);
    }
    return () => {
      dispatch(clearExportLogsCsvResult());
    };
  }, [csvFile]);

  const handleExportLogs = () => {
    dispatch(exportLogsCsv(filters));
  };

  const handleSubmit = (data) => {
    if (!isOpenCollapse) {
      return;
    }
    const trimmedData = trimFormData(data);
    dispatch(setLogSearchFilters(trimmedData));
    dispatch(getLogsCount(trimmedData));
    toggleCollapse();
  };

  return (
    <Formik
      ref={formikRef}
      onSubmit={handleSubmit}
      initialValues={FILTERS_TEMPLATE}
    >
      {(props) => {
        const { values } = props;
        const { dateFrom: dateFromValue, dateTo: dateToValue } = values;
        return (
          <div>
            <div className="logs-collapse-search">
              <Collapse isOpen={isOpenCollapse}>
                <Form className="search-logs-form">
                  <div className="search-logs-form-inner-container">
                    <Row>
                      <Col lg={2} className="pr-1">
                        <LabeledDatePicker
                          showTimeSelect
                          timeFormat="HH:mm"
                          dateFormat="dd.MM.yyyy HH:mm"
                          name="dateFrom"
                          placeholderText="Date From"
                          maxDate={dateToValue ? new Date(dateToValue) : null}
                          label=""
                          openToDate={new Date(moment())}
                        />
                      </Col>
                      <Col lg={2} className="pl-1">
                        <LabeledDatePicker
                          showTimeSelect
                          timeFormat="HH:mm"
                          dateFormat="dd.MM.yyyy HH:mm"
                          name="dateTo"
                          placeholderText="Date To"
                          minDate={
                            dateFromValue ? new Date(dateFromValue) : null
                          }
                          label=""
                          openToDate={new Date(moment())}
                        />
                      </Col>
                      <Col lg={4} className="form-group p-0 ">
                        <Field
                          type="text"
                          name="action"
                          placeholder="Action"
                          component={Input}
                        />
                      </Col>
                      <Col lg={4}>
                        <Field
                          type="text"
                          name="message"
                          placeholder="Message"
                          component={Input}
                        />
                      </Col>
                      <Col lg={8}>
                        <Field
                          isClearable
                          type="text"
                          name="type"
                          placeholder="Type"
                          component={ButtonSelectLogsType}
                          options={logTypeOptions}
                        />
                      </Col>
                      <Col lg={4}>
                        <Field
                          type="text"
                          name="createdByName"
                          placeholder="User"
                          component={Input}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        lg={12}
                        className="d-flex justify-content-center pt-4"
                      >
                        <ButtonWithSpinner
                          id="searchLogsSubmitButton"
                          text="Search"
                          outline
                          type="submit"
                          className="search-button-logs-list"
                          loading={isLoading}
                        />
                      </Col>
                    </Row>
                  </div>
                </Form>
                <div className="logs-filters-menu-collapse-button">
                  {entriesLogs.length ? (
                    <Clickable onClick={toggleCollapse}>
                      <CollapseButton />
                    </Clickable>
                  ) : null}
                </div>
              </Collapse>
            </div>
            <div className="logs-filters-menu-collapse-button">
              <Clickable onClick={toggleCollapse}>
                {!isOpenCollapse ? <CollapseOpenIcon /> : null}
              </Clickable>
            </div>
            <div className="logs-filters-main-container">
              {Object.values(filters).some(Boolean) ? (
                <div className="filters-menu-label">Filter Applied</div>
              ) : null}
              {Object.entries(filters).map(([key, value], i) => {
                return value ? (
                  <SingleFilter
                    filterKey={key}
                    filterValue={value}
                    clearFilter={() => clearSingleFilter(key)}
                  />
                ) : null;
              })}
              {Object.values(filters).some(Boolean) ? (
                <div
                  onClick={clearFilters}
                  className="filters-menu-label-clear"
                >
                  Clear All
                </div>
              ) : null}

              <div className="ml-auto">
                <ButtonExport
                  loading={isExportLoading}
                  forced={!Object.values(filters).some(Boolean)}
                  text="Export"
                  icon={<FiUpload size={20} />}
                  type="button"
                  onClick={handleExportLogs}
                />
              </div>
            </div>
          </div>
        );
      }}
    </Formik>
  );

  function clearFilters() {
    dispatch(setLogSearchFilters(FILTERS_TEMPLATE));
    formikRef.current.setValues(FILTERS_TEMPLATE);
    toggleCollapse();
  }

  function clearSingleFilter(filterKey) {
    let val = "";
    const currentFilters = { ...filters };

    dispatch(
      setLogSearchFilters({
        [filterKey]: val,
      })
    );

    currentFilters[filterKey] = val;
    formikRef.current.setFieldValue(filterKey, val);

    if (Object.values(currentFilters).filter(Boolean).length) {
      dispatch(getLogsCount(currentFilters));
    } else {
      toggleCollapse();
    }
  }

  function SingleFilter({
    filterKey = "",
    filterValue = "",
    clearFilter = () => {},
  }) {
    let currentValue = filterValue;
    if (filterKey === "type" && filterValue) {
      // set type label
      currentValue = logTypeOptions.find(
        (type) => type.value === filterValue
      ).label;
    }

    if ((filterKey === "dateFrom" || filterKey === "dateTo") && filterValue) {
      // format date
      currentValue = moment(new Date(filterValue)).format("DD.MM.yyyy HH:mm");
    }

    return (
      <div className="single-filter-box">
        <div className="single-filter-label">
          {LOGS_FILTERS_LABELS[filterKey]}
        </div>
        <div className="single-filter-chip">
          {currentValue}
          <FaTimes
            className="remove-filter-icon"
            onClick={clearFilter}
            color="#707070"
          />
        </div>
      </div>
    );
  }
};
SearchLogsForm.propTypes = {
  logTypeOptions: optionsType,
};

export default SearchLogsForm;



// WEBPACK FOOTER //
// ./src/features/log/components/search-logs-form/SearchLogsForm.jsx